import Http from '../../network/Http'
import {ApiResponse} from '../../network/response/ApiResponse'
import MeetingSimple from './model/MeetingSimple'
import {ContentType} from '../../network/Constants'
import NewMeetingComment from './model/NewMeetingComment'
import MeetingCommentDetail from './model/MeetingCommentDetail'
import {ATTENDANCE_STATUS, AVAILABLE_OR_NOT, VALID_OR_NOT} from '../../CodeMaster'
import {CommentReactedUser} from './model/MeetingComment'
import MeetingCommentView from './model/MeetingCommentView'
import {MeetingCommentFile} from './model/MeetingCommentFile'
import MeetingAttendanceResponse from './model/MeetingAttendanceResponse'
import NewMeetingAttendanceResponse from './model/NewMeetingAttendanceResponse'
import UpdateMeetingAttendanceResponse from './model/UpdateMeetingAttendanceResponse'
import moment from 'moment'
import DeletedComment from './model/DeletedComment'
import DeletedCommentView from './model/DeletedCommentView'
import DeletedCommentDetail from './model/DeletedCommentDetail'
import UpdateMeetingComment from './model/UpdateMeetingComment'
import {
    CommentReactedUserResponse,
    convertToCommentReactedUser,
    convertToMeetingComment,
    convertToMeetingCommentReply,
    convertToMeetingCommentView,
    MeetingAttendanceResponseResponse,
    MeetingCommentDetailResponse,
    MeetingResponse,
    MeetingSimpleResponse,
} from './MeetingReposioryCommons'
import {MeetingWithComments} from './model/MeetingWithComments'
import {FileDataForValidate} from './model/FileDataForValidate'
import {MeetingAttendanceNoResponse} from './model/MeetingAttendanceNoResponse'
import BaseMeetingAttendanceResponse from './model/BaseMeetingAttendanceResponse'
import {MeetingCommentOutline} from './model/MeetingCommentOutline'
import {CommentNotificationData} from './model/CommentNotificationData'
import {Room} from './model/Room'

export class MeetingRepositoryForResident {
    http: Http

    constructor(http: Http) {
        this.http = http
    }

    getMeetingsEndpoint(condominiumCd: string): string {
        return `/api/resident/condominiums/${condominiumCd}/meetings`
    }

    getRecentCommentsEndPoint(condominiumCd: string): string {
        return this.getMeetingsEndpoint(condominiumCd) + '/all/recent-comments'
    }

    getCommentsEndPoint(condominiumCd: string, meetingId: number): string {
        return this.getMeetingsEndpoint(condominiumCd) + `/${meetingId}/comments`
    }

    getCommentRepliesEndPoint(condominiumCd: string, meetingId: number, commentId: number): string {
        return this.getCommentsEndPoint(condominiumCd, meetingId) + `/${commentId}/replies`
    }

    async getMeetings(condominiumCd: string): Promise<MeetingSimple[]> {
        const apiResponseJSON: ApiResponse<MeetingSimpleResponse[]> = await this.http.get(
            this.getMeetingsEndpoint(condominiumCd),
        )
        const details = apiResponseJSON.details
        return details.map(detail => ({
            ...detail,
            attendanceReplyDeadline: detail.attendanceReplyDeadline === null ? null :
                moment(detail.attendanceReplyDeadline),
            proxyAttendanceAvailability: detail.proxyAttendanceAvailability === null ? null :
                AVAILABLE_OR_NOT.codeToEnumConstants(detail.proxyAttendanceAvailability),
        }))
    }

    async getRecentComments(condominiumCd: string): Promise<MeetingCommentOutline[]> {
        const apiResponseJSON: ApiResponse<MeetingCommentOutline[]> = await this.http.get(
            this.getRecentCommentsEndPoint(condominiumCd),
        )
        const details = apiResponseJSON.details

        return details.map(detail => new MeetingCommentOutline(
            detail.meetingId,
            detail.meetingCommentId,
            detail.replySourceId,
            detail.replySourceContent,
            detail.meetingName,
            detail.content,
            detail.familyName,
            detail.firstName,
            detail.roleId,
            detail.roomList.map(room => {
                return Room.from(room)
            }),
            detail.isWithinTerm,
            detail.hasMailAddress,
            detail.registerDatetime,
        ))
    }

    async getMeetingWithComments(condominiumCd: string, meetingId: number): Promise<MeetingWithComments> {
        const apiResponseJSON: ApiResponse<MeetingResponse> = await this.http.get(
            this.getMeetingsEndpoint(condominiumCd) + `/${meetingId}`,
        )
        const details = apiResponseJSON.details
        const meetingCommentList: (MeetingCommentView | DeletedCommentView)[] = details.meetingCommentList
            .map(comment => convertToMeetingCommentView(comment))

        return new MeetingWithComments(
            details.id,
            details.name,
            details.date,
            details.startTime,
            details.endTime,
            details.place,
            details.url,
            details.contents,
            details.memo,
            details.attendanceReplyDeadline === null ? null :
                moment(details.attendanceReplyDeadline),
            details.attendanceReplyRequestDatetime === null ? null :
                moment(details.attendanceReplyRequestDatetime),
            VALID_OR_NOT.codeToEnumConstants(details.manageAttendance),
            details.proxyAttendanceAvailability === null ? null :
                AVAILABLE_OR_NOT.codeToEnumConstants(details.proxyAttendanceAvailability),
            details.meetingAnnounceNotifyDatetime === null ? null :
                moment(details.meetingAnnounceNotifyDatetime),
            details.meetingAttachmentFileList,
            details.meetingAttachmentLinkList,
            details.meetingReportList,
            meetingCommentList,
            details.version,
        )
    }

    async getCommentDetail(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
    ): Promise<MeetingCommentDetail | DeletedCommentDetail> {
        const apiResponseJSON: ApiResponse<MeetingCommentDetailResponse> = await this.http.get(
            this.getCommentsEndPoint(condominiumCd, meetingId) + `/${commentId}`,
        )

        const details = apiResponseJSON.details
        const commentReplyList = details.commentReplyList
            .map(reply => convertToMeetingCommentReply(reply, commentId))

        const converted = convertToMeetingComment(details)

        if (converted instanceof DeletedComment) {
            return new DeletedCommentDetail(
                converted.meetingCommentId,
                converted.message,
                commentReplyList,
            )
        } else {
            return new MeetingCommentDetail(
                converted.meetingCommentId,
                converted.content,
                converted.user,
                converted.updateDatetime,
                converted.commentReactedUserList,
                converted.attachmentList,
                converted.isYou,
                converted.version,
                commentReplyList,
            )
        }
    }

    async registerComment(
        condominiumCd: string,
        meetingId: number,
        newMeetingComment: NewMeetingComment,
    ): Promise<number> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            this.getCommentsEndPoint(condominiumCd, meetingId),
            newMeetingComment,
            ContentType.JSON,
        )

        return apiResponseJSON.details
    }

    async registerCommentReply(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        newMeetingComment: NewMeetingComment,
    ): Promise<number> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            this.getCommentRepliesEndPoint(condominiumCd, meetingId, commentId),
            newMeetingComment,
            ContentType.JSON,
        )

        return apiResponseJSON.details
    }

    async updateComment(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        updateMeetingComment: UpdateMeetingComment,
    ): Promise<void> {
        await this.http.put(
            this.getCommentsEndPoint(condominiumCd, meetingId) + `/${commentId}`,
            updateMeetingComment,
        )
    }

    async updateCommentReply(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
        updateMeetingComment: UpdateMeetingComment,
    ): Promise<void> {
        await this.http.put(
            this.getCommentRepliesEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}`,
            updateMeetingComment,
        )
    }

    async deleteComment(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
    ): Promise<void> {
        await this.http.delete(
            this.getCommentsEndPoint(condominiumCd, meetingId) + `/${commentId}`,
        )
    }

    async deleteCommentReply(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
    ): Promise<void> {
        await this.http.delete(
            this.getCommentRepliesEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}`,
        )
    }

    async registerCommentFile(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentFile: MeetingCommentFile,
    ): Promise<void> {
        await this.http.post(
            this.getCommentsEndPoint(condominiumCd, meetingId) + `/${commentId}/files`,
            commentFile,
            ContentType.JSON,
        )
    }

    async registerCommentReplyFile(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
        commentReplyFile: MeetingCommentFile,
    ): Promise<void> {
        await this.http.post(
            this.getCommentRepliesEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}/files`,
            commentReplyFile,
            ContentType.JSON,
        )
    }

    async updateCommentReaction(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        isReaction: boolean,
    ): Promise<CommentReactedUser[]> {
        const apiResponseJSON: ApiResponse<CommentReactedUserResponse[]> = await this.http.put(
            this.getCommentsEndPoint(condominiumCd, meetingId) + `/${commentId}/reaction`,
            {isReaction: isReaction},
        )

        const details = apiResponseJSON.details
        return details.map(commentReactedUser => convertToCommentReactedUser(commentReactedUser))
    }

    async updateCommentReplyReaction(
        condominiumCd: string,
        meetingId: number,
        commentId: number,
        commentReplyId: number,
        isReaction: boolean,
    ): Promise<CommentReactedUser[]> {
        const apiResponseJSON: ApiResponse<CommentReactedUserResponse[]> = await this.http.put(
            this.getCommentRepliesEndPoint(condominiumCd, meetingId, commentId) + `/${commentReplyId}/reaction`,
            {isReaction: isReaction},
        )

        const details = apiResponseJSON.details
        return details.map(reactionUser => convertToCommentReactedUser(reactionUser))
    }

    async getAttendance(condominiumCd: string, meetingId: number): Promise<BaseMeetingAttendanceResponse | 'Observer'> {
        const apiResponseJSON: ApiResponse<MeetingAttendanceResponseResponse> = await this.http.get(
            this.getMeetingsEndpoint(condominiumCd) + `/${meetingId}/attendance`,
        )

        const details = apiResponseJSON.details
        if (details === null) {
            return 'Observer'
        }

        if (details.attendanceId === 0) {
            return new MeetingAttendanceNoResponse()
        }

        return new MeetingAttendanceResponse(
            ATTENDANCE_STATUS.codeToEnumConstants(details.attendanceStatus),
            details.attendanceId,
            details.note,
            details.proxyUserName,
            details.proxyUserRelationship,
            moment(details.responseDateTime),
            details.agentRespondentAdminId,
            details.agentRespondentRoleName,
            details.agentRespondentName,
            details.version,
        )
    }

    async registerAttendance(
        condominiumCd: string,
        meetingId: number,
        newMeetingAttendance: NewMeetingAttendanceResponse,
    ): Promise<number> {
        const apiResponseJSON: ApiResponse<number> = await this.http.post(
            this.getMeetingsEndpoint(condominiumCd) + `/${meetingId}/attendance`,
            {
                ...newMeetingAttendance,
                attendanceStatus: newMeetingAttendance.attendanceStatus.value,
            },
            ContentType.JSON,
        )

        return apiResponseJSON.details
    }

    async updateAttendance(
        condominiumCd: string,
        meetingId: number,
        attendanceId: number,
        updateMeetingAttendance: UpdateMeetingAttendanceResponse,
    ): Promise<void> {
        await this.http.put(
            this.getMeetingsEndpoint(condominiumCd) + `/${meetingId}/attendance/${attendanceId}`,
            {
                ...updateMeetingAttendance,
                attendanceStatus: updateMeetingAttendance.attendanceStatus.value,
            },
        )
    }

    async getMeetingsNeedsAttendanceReply(condominiumCd: string): Promise<MeetingSimple[]> {
        const apiResponseJSON: ApiResponse<MeetingSimpleResponse[]> = await this.http.get(
            this.getMeetingsEndpoint(condominiumCd) + '?needsAttendanceReply=true',
        )
        const details = apiResponseJSON.details
        return details.map(detail => ({
            ...detail,
            attendanceReplyDeadline: detail.attendanceReplyDeadline === null ? null :
                moment(detail.attendanceReplyDeadline),
            proxyAttendanceAvailability: detail.proxyAttendanceAvailability === null ? null :
                AVAILABLE_OR_NOT.codeToEnumConstants(detail.proxyAttendanceAvailability),
        }))
    }

    async validateParallelFileUpload(
        fileDataForValidate: FileDataForValidate,
    ): Promise<void> {
        await this.http.post(
            '/api/common/validate/files/parallel-file-upload',
            fileDataForValidate,
            ContentType.JSON,
        )
    }

    async sendCommentNotification(
        condominiumCd: string,
        commentNotificationData: CommentNotificationData,
    ): Promise<void> {
        await this.http.post(
            `/api/resident/condominiums/${condominiumCd}/managers/notifications/mail-comment-notifications`,
            commentNotificationData,
            ContentType.JSON,
        )
    }
}
